import React, { useState, useEffect } from 'react';
import { Settings, DollarSign, Gift, Eye, Image, MessageSquare, Save, AlertCircle, Users } from 'lucide-react';
import { getCreatorPoolSettings, updateCreatorPoolSettings } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

export function CreatorPoolSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await getCreatorPoolSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching pool settings:', error);
      toast.error('Failed to load pool settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateCreatorPoolSettings({
        opt_out_of_pool: settings.opt_out_of_pool,
        monthly_soft_cap: settings.monthly_soft_cap,
        offer_early_access: settings.offer_early_access,
        offer_ad_free: settings.offer_ad_free,
        offer_bonus_art: settings.offer_bonus_art,
        custom_thank_you: settings.custom_thank_you
      });
      toast.success('Pool settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  if (loading) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
        <div className="h-8 w-48 bg-[var(--border)] rounded mb-6" />
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="creator-pool-settings">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={18} className="text-green-500" />
            <span className="text-sm text-[var(--text-secondary)]">Pool Earnings</span>
          </div>
          <p className="text-2xl font-teko">${settings.total_pool_earnings?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={18} className="text-blue-500" />
            <span className="text-sm text-[var(--text-secondary)]">Supporters</span>
          </div>
          <p className="text-2xl font-teko">{settings.supporters_count || 0}</p>
        </div>
      </div>

      {/* Main Settings */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="p-6 border-b border-[var(--border)]">
          <h2 className="text-xl font-teko flex items-center gap-2">
            <Settings size={20} />
            Revenue Pool Settings
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Control how you participate in the smart revenue sharing system
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Opt Out */}
          <div className="flex items-start justify-between p-4 bg-[var(--background)] rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <AlertCircle size={18} className="text-yellow-500" />
                <h3 className="font-medium">Opt Out of Revenue Pool</h3>
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                If enabled, you won't receive any revenue from the subscriber pool. 
                Choose this if you prefer ads-only monetization.
              </p>
            </div>
            <button
              onClick={() => toggleSetting('opt_out_of_pool')}
              className={`w-14 h-8 rounded-full transition-colors flex items-center px-1 ${
                settings.opt_out_of_pool ? 'bg-red-500' : 'bg-gray-500'
              }`}
            >
              <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                settings.opt_out_of_pool ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Soft Cap */}
          <div className="p-4 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={18} className="text-green-500" />
              <h3 className="font-medium">Monthly Soft Cap</h3>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              Set a maximum amount you want to receive monthly. 
              Excess will be redistributed to other creators.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                step="1"
                min="0"
                value={settings.monthly_soft_cap || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  monthly_soft_cap: e.target.value ? parseFloat(e.target.value) : null
                })}
                placeholder="No cap"
                className="w-32 px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg"
              />
              <span className="text-sm text-[var(--text-secondary)]">
                Leave empty for no cap
              </span>
            </div>
          </div>

          {/* Supporter Perks */}
          <div>
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <Gift size={18} className="text-purple-500" />
              Supporter Perks
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Choose what perks your supporters receive:
            </p>
            
            <div className="grid gap-3">
              <label className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg cursor-pointer hover:bg-[var(--background)]/80">
                <div className="flex items-center gap-3">
                  <Eye size={18} className="text-blue-500" />
                  <div>
                    <p className="font-medium">Early Access</p>
                    <p className="text-xs text-[var(--text-secondary)]">Supporters see new episodes before public release</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.offer_early_access}
                  onChange={() => toggleSetting('offer_early_access')}
                  className="w-5 h-5 rounded accent-[var(--primary)]"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg cursor-pointer hover:bg-[var(--background)]/80">
                <div className="flex items-center gap-3">
                  <Eye size={18} className="text-green-500" />
                  <div>
                    <p className="font-medium">Ad-Free Viewing</p>
                    <p className="text-xs text-[var(--text-secondary)]">No ads shown to your supporters</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.offer_ad_free}
                  onChange={() => toggleSetting('offer_ad_free')}
                  className="w-5 h-5 rounded accent-[var(--primary)]"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg cursor-pointer hover:bg-[var(--background)]/80">
                <div className="flex items-center gap-3">
                  <Image size={18} className="text-purple-500" />
                  <div>
                    <p className="font-medium">Bonus Art Access</p>
                    <p className="text-xs text-[var(--text-secondary)]">Exclusive artwork and behind-the-scenes content</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.offer_bonus_art}
                  onChange={() => toggleSetting('offer_bonus_art')}
                  className="w-5 h-5 rounded accent-[var(--primary)]"
                />
              </label>
            </div>
          </div>

          {/* Custom Thank You */}
          <div className="p-4 bg-[var(--background)] rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={18} className="text-pink-500" />
              <h3 className="font-medium">Custom Thank You Message</h3>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              A personal message shown to your supporters on their monthly receipt
            </p>
            <textarea
              value={settings.custom_thank_you || ''}
              onChange={(e) => setSettings({ ...settings, custom_thank_you: e.target.value })}
              placeholder="Thank you for supporting my work! Your support means the world to me..."
              className="w-full px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg h-24 resize-none"
              maxLength={200}
            />
            <p className="text-xs text-[var(--text-secondary)] text-right mt-1">
              {(settings.custom_thank_you || '').length}/200
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="p-6 border-t border-[var(--border)] bg-[var(--background)]">
          <Button onClick={handleSave} disabled={saving} className="w-full">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Philosophy Note */}
      <div className="p-4 bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-lg">
        <p className="text-sm text-center italic">
          "This isn't charity. It's revenue sharing based on what readers actually enjoy. 
          You earn what you're worth."
        </p>
      </div>
    </div>
  );
}

export default CreatorPoolSettings;
